import React, { FC, Suspense, lazy, useEffect, useRef } from 'react';

import { useTranslation } from '@crehana/i18n';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';

import SimpleErrorBoundary from 'Jsx/Shared/ErrorBoundaries/Simple';
import Media from 'Jsx/Shared/ui/Media';
import ExitPopup from 'Jsx/Shared/ExitPopup';
import ClientSideComponent from 'Jsx/Shared/ClientSideComponent';
import PopupMaraton from 'Jsx/Shared/PopupMaraton';
import IdleComponent from 'Jsx/Shared/IdleComponent';

import ExperimentLayout from 'Jsx/Shared/ExperimentLayout';

import { isFilterApplied } from '../components/FiltersDesktop/utils';
import OrderByMobile from '../components/OrderByMobile';
import FiltersMobile from '../components/FiltersMobile';
import FilterLinks, { FilterLinksMobile } from '../components/FilterLinks';
import LandingV2 from '../components/LandingV2';
import Results from '../components/Results';
import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import BannerPremiumTrialMobile from '../components/BannerPremiumTrial/Mobile';
import Container from '../components/Container';
import { useAppContext } from '../context/AppContext';
import FilterChips from '../components/FilterChips';
import FilterButtonWrapper from '../components/FilterButtonWrapper';
import FiltersDesktop from '../components/FiltersDesktop';

const LazyCoursesRecommendedForMe = lazy(
  () =>
    import(
      /* webpackChunkName: "catalog-recommended-for-me" */
      '../components/CourseRecommended/CoursesRecommendedForMe'
    ),
);

interface AppProps {
  shouldRenderCourseFreeBanner?: boolean;
  trackPageRender(category: string, subCategory: string): void;
  initialSeoLongDescription: string | null;
  featureFlagStartMarathonPopup: boolean;
  shouldRenderSliderHeader: boolean;
  forceCloseFloatingFilter?: boolean;
}

const App: FC<AppProps> = ({
  shouldRenderCourseFreeBanner,
  trackPageRender,
  initialSeoLongDescription,
  featureFlagStartMarathonPopup,
  shouldRenderSliderHeader,
  forceCloseFloatingFilter = false,
}) => {
  const {
    filters,
    filters: { allCourses, showRecommendedForMeCourses },
    orderBy,
  } = useAppContext();
  const { t } = useTranslation();
  const { loading: loadingMeQuery, me } = useMe();

  const menuNodeElement = useRef<HTMLDivElement | null>(null);
  const [seoLongDescription, setSeoLongDescription] = React.useState<
    string | null
  >(initialSeoLongDescription);
  const getLongDescription = (value: string | null) =>
    setSeoLongDescription(value);

  const filterApplied =
    isFilterApplied(filters) || orderBy.itemSelected?.slug !== 'online';

  useEffect(() => {
    menuNodeElement.current = document.querySelector('#MainMenuComponent');
  }, []);

  const getHeightMenu = () => {
    if (!menuNodeElement.current) {
      return 72;
    }
    const { height } = menuNodeElement.current.getBoundingClientRect();

    return height;
  };

  const scrollTop = () => {
    if (typeof document !== 'undefined') {
      const scrollTopNode = document.getElementById('scroll-to');

      if (scrollTopNode) {
        const scrollTopHeight = scrollTopNode.offsetTop - getHeightMenu();

        if (window.scrollY > scrollTopHeight) {
          window.scroll({
            top: scrollTopHeight,
            left: 0,
            behavior: 'smooth',
          });
        }
      }
    }
  };

  const isOrganization = Boolean(!loadingMeQuery && me?.hasOrganization);
  const shoulRenderExitPopup =
    shouldRenderCourseFreeBanner && !loadingMeQuery && !me;
  const isPremiumMember = Boolean(!loadingMeQuery && me?.isPremiumMember);
  const shouldRenderPremiumBar = !isPremiumMember && !isOrganization;
  const shouldRenderFloatingFilter =
    !forceCloseFloatingFilter && !showRecommendedForMeCourses;

  const shouldRenderPopupMarathon =
    featureFlagStartMarathonPopup &&
    !loadingMeQuery &&
    me &&
    !me.isPremiumMember;

  return (
    <>
      <Container flexCol>
        <Breadcrumbs
          trackPageRender={trackPageRender}
          getLongDescription={getLongDescription}
        />

        <Media until="md">
          <FilterLinksMobile className="flex-1 w-full mb-24" />
        </Media>
      </Container>

      <div
        id="scroll-to"
        className="relative w-full h-0 bg-white md:bg-inherit"
      />

      {shouldRenderFloatingFilter && (
        <ClientSideComponent>
          <Media until="md">
            <FilterButtonWrapper isPremium={shouldRenderPremiumBar}>
              <div className="container-card">
                <FiltersMobile />
                <div className="container-card--separator" />
                <OrderByMobile />
              </div>
            </FilterButtonWrapper>
          </Media>
        </ClientSideComponent>
      )}
      {shouldRenderCourseFreeBanner && (
        <Media until="sm">
          <BannerPremiumTrialMobile />
        </Media>
      )}

      {shouldRenderPopupMarathon && (
        <ClientSideComponent>
          <PopupMaraton
            enable={shouldRenderPopupMarathon}
            pageName="Catalog"
            ctaLabel={t('POPUP_MARATHON_CTA_LABEL')}
            title={t('POPUP_MARATHON_TITLE')}
          />
        </ClientSideComponent>
      )}

      <Container
        isOrganization={isOrganization}
        className="mt-20 mb-20 tw-font-pop"
      >
        {/* Filters */}
        {!showRecommendedForMeCourses && (
          <aside
            id="filter__wrapper"
            className="hidden lg:flex flex-col mr-32 w-full max-w-280 min-w-280"
          >
            <FilterLinks />

            <div className="sticky top-0 self-start w-full">
              <SimpleErrorBoundary>
                <ClientSideComponent>
                  <Media from="md">
                    <Suspense
                      fallback={
                        <div className="w-full h-full">
                          <ul className="w-full h-160 bg-gray-lighter" />
                        </div>
                      }
                    >
                      <FilterChips />
                    </Suspense>
                  </Media>
                </ClientSideComponent>

                <FiltersDesktop scrollTop={scrollTop} />
              </SimpleErrorBoundary>
            </div>
          </aside>
        )}

        {/* Results */}
        {(allCourses || filterApplied) && !showRecommendedForMeCourses && (
          <ExperimentLayout>
            <Results
              scrollTop={scrollTop}
              cleanFiltersMsg={t('CLEAN_FILTERS')}
              shouldRenderCourseFreeBanner={shouldRenderCourseFreeBanner}
              seoLongDescription={seoLongDescription}
              shouldRenderSliderHeader={shouldRenderSliderHeader}
              allCourses={allCourses}
            />
          </ExperimentLayout>
        )}

        {showRecommendedForMeCourses && (
          <ClientSideComponent>
            <Suspense fallback={null}>
              <LazyCoursesRecommendedForMe scrollTop={scrollTop} />
            </Suspense>
          </ClientSideComponent>
        )}

        <IdleComponent>
          {shoulRenderExitPopup && (
            <ExitPopup
              title={t('EXIT_POPUP_TITLE')}
              subtitle={t('EXIT_POPUP_SUB_TITLE')}
              labelCta={t('EXIT_POPUP_CTA')}
              isRenderedIn="Landing Free Courses"
            />
          )}
        </IdleComponent>

        {!allCourses && !filterApplied && !showRecommendedForMeCourses && (
          <LandingV2 me={me} />
        )}
      </Container>
    </>
  );
};

export default App;
